<script setup lang="ts">
import { CheckIcon } from '@lucide/vue'
import { reactiveOmit } from '@vueuse/core'
import type { SelectItemProps } from 'reka-ui'
import { SelectItem, SelectItemIndicator, SelectItemText, useForwardProps } from 'reka-ui'
import { type HTMLAttributes, inject, onUnmounted } from 'vue'
import { cn } from '../../../lib/utils'
import { NATIVE_OPTIONS } from './nativeOptions'

/**
 * A highlighted option takes the accent pair (cyan ground, black type, BG §18.3), the same as the command
 * button. No outline suppression: keyboard focus still draws the `@layer base` ring (BG §22.3).
 */
const props = defineProps<SelectItemProps & { class?: HTMLAttributes['class'] }>()

const delegatedProps = reactiveOmit(props, 'class')

const forwardedProps = useForwardProps(delegatedProps)

// Known to the Select's native mirror too, so a value set on the hidden field resolves to this item.
const unregister = inject(NATIVE_OPTIONS, null)?.register(() => ({
    value: props.value,
    disabled: props.disabled ?? false,
}))
onUnmounted(() => unregister?.())
</script>

<template>
  <SelectItem
    data-slot="select-item"
    v-bind="forwardedProps"
    :class="
      cn(
        'focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2',
        props.class,
      )
    "
  >
    <span class="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectItemIndicator>
        <slot name="indicator-icon">
          <CheckIcon class="size-4" />
        </slot>
      </SelectItemIndicator>
    </span>

    <SelectItemText>
      <slot />
    </SelectItemText>
  </SelectItem>
</template>
